import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material';
import {
  CloudUpload,
  Save,
  Cancel,
  Delete,
  Add,
} from '@mui/icons-material';
import { excelMappingApi } from '../api/excelMapping';
import type { ExcelMappingConfig, ExcelMappingField, TargetField } from '../api/excelMapping';
import { useTranslation } from 'react-i18next';

interface ExcelMappingConfigFormProps {
  config?: ExcelMappingConfig;
  onSave: (config: ExcelMappingConfig) => void;
  onCancel: () => void;
}

const ExcelMappingConfigForm: React.FC<ExcelMappingConfigFormProps> = ({
  config,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    isActive: boolean;
    isDefault?: boolean;
    reportType?: string;
    reportTemplate?: string;
    uploadFileTypes?: string[];
  }>({
    name: '',
    description: '',
    isActive: true,
    reportType: '',
    reportTemplate: '',
    uploadFileTypes: ['pdf'], // Por defecto solo PDF
  });
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelColumns, setExcelColumns] = useState<string[]>([]);
  const [excelPreview, setExcelPreview] = useState<any[]>([]);
  const [targetFields, setTargetFields] = useState<TargetField[]>([]);
  const [mappingFields, setMappingFields] = useState<ExcelMappingField[]>([]);
  const [reportTypes, setReportTypes] = useState<string[]>([]);
  // Puede ser array de strings o de objetos { id, name, ... }
  const [reportTemplates, setReportTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  
  const applyCustomFlags = (fields: ExcelMappingField[], targets: TargetField[]): ExcelMappingField[] => {
    const allowed = new Set((targets || []).map(t => String(t.targetField)));
    return (fields || []).map((f: any) => ({
      ...f,
      custom: !allowed.has(String(f.targetField)),
    }));
  };

  useEffect(() => {
    loadTargetFields();
    loadReportTypes();
    if (config) {
      setFormData({
        name: config.name,
        description: config.description || '',
        isActive: config.isActive || true,
        // ensure default flag is reflected when editing
        // using index signature to avoid widening the type
        ...(config as any).isDefault !== undefined ? { isDefault: (config as any).isDefault } : {},
        reportType: (config as any).reportType || '',
        reportTemplate: (config as any).reportTemplate || '',
        uploadFileTypes: (config as any).uploadFileTypes && Array.isArray((config as any).uploadFileTypes) 
          ? (config as any).uploadFileTypes 
          : ['pdf'], // Por defecto solo PDF
      });
      setExcelColumns(config.excelColumns || []);
      // Inicialmente aplicar custom según campos estándar (hasta que carguen templates)
      setMappingFields(applyCustomFlags(config.fields || [], targetFields));

      // Si hay reportType en la config, cargar templates y seleccionar el existente
      (async () => {
        try {
          const presetType = (config as any).reportType || '';
          if (presetType) {
            const resp = await excelMappingApi.getTemplatesByType(presetType);
            const raw = resp?.data?.data || resp?.data || {};
            const templates = Array.isArray((raw as any).templates) ? (raw as any).templates : Array.isArray(raw) ? raw : [];
            setReportTemplates(templates);

            const presetTemplate = (config as any).reportTemplate || '';
            if (presetTemplate) {
              // Encontrar el template y poblar variables como targetFields si no hay mappingFields
              const selected = templates.find((tpl: any) => {
                if (typeof tpl === 'string') return String(tpl) === String(presetTemplate);
                const label = tpl?.name ?? tpl?.id;
                return String(label) === String(presetTemplate);
              });
              const variables: string[] = Array.isArray((selected as any)?.variables) ? (selected as any).variables : [];
              if (variables.length > 0) {
                const templateTargetFields: TargetField[] = variables.map((v) => ({
                  targetField: String(v),
                  targetFieldLabel: String(v),
                  isRequired: true,
                }));
                setTargetFields(templateTargetFields);
                // Reaplicar custom flags contra variables de template
                setMappingFields(prev => applyCustomFlags(prev, templateTargetFields));
                if (!config.fields || config.fields.length === 0) {
                  const initialMapping: ExcelMappingField[] = variables.map((v) => ({
                    targetField: String(v),
                    targetFieldLabel: String(v),
                    isRequired: true,
                  }));
                  setMappingFields(initialMapping);
                }
              }
            }
          }
        } catch {}
      })();
    } else {
      initializeDefaultFields();
    }
  }, [config]);

  // Si cargan/varían los targetFields (por templates) y ya hay mappingFields, ajustar custom
  useEffect(() => {
    if (mappingFields.length > 0 && targetFields.length > 0) {
      setMappingFields(prev => applyCustomFlags(prev, targetFields));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetFields]);

  const loadTargetFields = async () => {
    try {
      const response = await excelMappingApi.getTargetFields();
      if (response.success && response.data) {
        setTargetFields(response.data);
      }
    } catch (err) {
      console.error(t('excelMapping.errors.loadTargets', 'Error al cargar campos objetivo:'), err);
    }
  };

  const loadReportTypes = async () => {
    try {
      const resp = await excelMappingApi.getReportTypes();
      const data = resp?.data?.data || resp?.data || [];
      const types = Array.isArray(data?.available_types) ? data.available_types : Array.isArray(data) ? data : [];
      setReportTypes(types);
    } catch {}
  };

  const onChangeReportType = async (value: string) => {
    setFormData(prev => ({ ...prev, reportType: value, reportTemplate: '' } as any));
    try {
      const resp = await excelMappingApi.getTemplatesByType(value);
      const raw = resp?.data?.data || resp?.data || {};
      // El servicio puede devolver { success, templates: [{id,name,...}], count } o directamente el objeto
      const templates = Array.isArray((raw as any).templates) ? (raw as any).templates : Array.isArray(raw) ? raw : [];
      setReportTemplates(templates);
    } catch {
      setReportTemplates([]);
    }
  };

  const onChangeReportTemplate = (value: string) => {
    setFormData(prev => ({ ...prev, reportTemplate: value } as any));
    try {
      // Buscar el template seleccionado
      const selected = reportTemplates.find((tpl: any) => {
        if (typeof tpl === 'string') return tpl === value;
        const label = tpl?.name ?? tpl?.id;
        return String(label) === value;
      });
      const variables: string[] = Array.isArray(selected?.variables) ? selected.variables : [];
      if (variables.length > 0) {
        // Construir lista de campos objetivo a partir de las variables del template
        const templateTargetFields: TargetField[] = variables.map((v) => ({
          targetField: String(v),
          targetFieldLabel: String(v),
          isRequired: true,
        }));
        setTargetFields(templateTargetFields);
        // Si no hay mapeo aún, prellenar con los campos del template (sin excelColumn)
        if (mappingFields.length === 0) {
          const initialMapping: ExcelMappingField[] = variables.map((v) => ({
            targetField: String(v),
            targetFieldLabel: String(v),
            isRequired: true,
          }));
          setMappingFields(initialMapping);
        }
      }
    } catch {}
  };

  const initializeDefaultFields = () => {
    const defaultFields: ExcelMappingField[] = [
      { targetField: 'nif', targetFieldLabel: 'NIF/DNI', isRequired: true },
      { targetField: 'name', targetFieldLabel: 'Nombre', isRequired: true },
      { targetField: 'email', targetFieldLabel: 'Email', isRequired: false },
      { targetField: 'phone', targetFieldLabel: 'Teléfono', isRequired: false },
    ];
    setMappingFields(defaultFields);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const response = await excelMappingApi.uploadExcelForMapping(file);
      if (response.success && response.data) {
        setExcelFile(file);
        setExcelColumns(response.data.columns);
        setExcelPreview(response.data.preview);
      } else {
        setError(response.message || 'Error al procesar el archivo Excel');
      }
    } catch (err) {
      setError(t('excelMapping.errors.processExcel', 'Error al procesar el archivo Excel'));
    } finally {
      setUploading(false);
    }
  };

  const handleFieldMappingChange = (index: number, field: Partial<ExcelMappingField>) => {
    const updatedFields = [...mappingFields];
    updatedFields[index] = { ...updatedFields[index], ...field };
    setMappingFields(updatedFields);
  };

  const addField = () => {
    const newField: ExcelMappingField = {
      targetField: '',
      targetFieldLabel: '',
      isRequired: false,
    };
    setMappingFields([...mappingFields, newField]);
  };

  const removeField = (index: number) => {
    const updatedFields = mappingFields.filter((_, i) => i !== index);
    setMappingFields(updatedFields);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError(t('common.nameRequired', 'El nombre es obligatorio'));
      return;
    }

    if (!excelFile && !config) {
      setError(t('excelMapping.errors.mustUpload', 'Debe cargar un archivo Excel'));
      return;
    }

    if (mappingFields.length === 0) {
      setError(t('excelMapping.errors.atLeastOneField', 'Debe configurar al menos un campo de mapeo'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const configData: Omit<ExcelMappingConfig, 'id' | 'createdAt' | 'updatedAt'> = {
        name: formData.name,
        description: formData.description,
        excelFileName: excelFile?.name || config?.excelFileName || '',
        excelFilePath: config?.excelFilePath || '', // En un caso real, esto se manejaría en el backend
        excelColumns,
        isActive: formData.isActive,
        isDefault: (formData as any).isDefault,
        fields: mappingFields.map((f: any) => {
          const { custom, ...rest } = f;
          return rest;
        }),
        reportType: formData.reportType || null,
        reportTemplate: formData.reportTemplate || null,
        uploadFileTypes: formData.uploadFileTypes && formData.uploadFileTypes.length > 0 
          ? formData.uploadFileTypes 
          : ['pdf'], // Por defecto solo PDF
      };

      let response;
      if (config) {
        response = await excelMappingApi.updateConfig(config.id!, configData);
      } else {
        response = await excelMappingApi.createConfig(configData);
      }

      if (response.success && response.data) {
        onSave(Array.isArray(response.data) ? response.data[0] : response.data);
      } else {
        setError(response.message || t('excelMapping.errors.save', 'Error al guardar la configuración'));
      }
    } catch (err) {
      setError(t('excelMapping.errors.save', 'Error al guardar la configuración'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {config ? t('excelMapping.editConfig', 'Editar Configuración') : t('excelMapping.newConfig', 'Nueva Configuración')}
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Cancel />}
            onClick={onCancel}
            sx={{ mr: 1 }}
          >
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : t('common.save', 'Guardar')}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Tipo de informe y Template */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('excelMapping.reportSettings', 'Configuración de Informe')}
              </Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel>{t('excelMapping.reportType', 'Tipo de informe')}</InputLabel>
                <Select
                  value={(formData as any).reportType || ''}
                  label={t('excelMapping.reportType', 'Tipo de informe')}
                  onChange={(e) => onChangeReportType(String(e.target.value))}
                >
                  {reportTypes.map((rt) => (
                    <MenuItem key={rt} value={rt}>{rt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal" disabled={!reportTemplates.length}>
                <InputLabel>{t('excelMapping.reportTemplate', 'Template')}</InputLabel>
                <Select
                  value={(formData as any).reportTemplate || ''}
                  label={t('excelMapping.reportTemplate', 'Template')}
                  onChange={(e) => onChangeReportTemplate(String(e.target.value))}
                >
                  {reportTemplates.map((tpl, idx) => {
                    const label = typeof tpl === 'string' ? tpl : (tpl.name ?? tpl.id ?? `template_${idx}`);
                    const value = typeof tpl === 'string' ? tpl : (String(tpl.name ?? tpl.id ?? `template_${idx}`));
                    const key = typeof tpl === 'string' ? tpl : String(tpl.id ?? tpl.name ?? idx);
                    return (
                      <MenuItem key={key} value={value}>{label}</MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>
        {/* Información básica */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('excelMapping.basicInfo', 'Información Básica')}
              </Typography>
              
              <TextField
                fullWidth
                label={t('excelMapping.configName', 'Nombre de la configuración')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="normal"
                required
              />

              <TextField
                fullWidth
                label={t('common.description', 'Descripción')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                margin="normal"
                multiline
                rows={3}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label={t('excelMapping.activeConfig', 'Configuración activa')}
                sx={{ mt: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean((formData as any).isDefault)}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked } as any)}
                  />
                }
                label={t('excelMapping.markDefault', 'Marcar como predeterminada')}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Configuración de archivos a subir */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('excelMapping.uploadFileTypes', 'Archivos a subir a Nextcloud')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('excelMapping.uploadFileTypesDescription', 'Selecciona qué tipos de archivo se subirán a Nextcloud después de generar los documentos')}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.uploadFileTypes?.includes('pdf') ?? true}
                    onChange={(e) => {
                      const currentTypes = formData.uploadFileTypes || ['pdf'];
                      const newTypes = e.target.checked
                        ? [...currentTypes.filter(t => t !== 'pdf'), 'pdf']
                        : currentTypes.filter(t => t !== 'pdf');
                      // Asegurar que al menos uno esté seleccionado
                      if (newTypes.length === 0 && !formData.uploadFileTypes?.includes('docx')) {
                        setFormData({ ...formData, uploadFileTypes: ['pdf'] });
                      } else {
                        setFormData({ ...formData, uploadFileTypes: newTypes.length > 0 ? newTypes : ['pdf'] });
                      }
                    }}
                  />
                }
                label="PDF"
                sx={{ display: 'block', mb: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.uploadFileTypes?.includes('docx') ?? false}
                    onChange={(e) => {
                      const currentTypes = formData.uploadFileTypes || ['pdf'];
                      const newTypes = e.target.checked
                        ? [...currentTypes.filter(t => t !== 'docx'), 'docx']
                        : currentTypes.filter(t => t !== 'docx');
                      // Asegurar que al menos uno esté seleccionado
                      if (newTypes.length === 0) {
                        setFormData({ ...formData, uploadFileTypes: ['pdf'] });
                      } else {
                        setFormData({ ...formData, uploadFileTypes: newTypes });
                      }
                    }}
                  />
                }
                label="DOCX"
                sx={{ display: 'block' }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Carga de archivo Excel */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('excelMapping.excelFile', 'Archivo Excel')}
              </Typography>

              {!config && (
                <Box>
                  <input
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    id="excel-file-upload"
                    type="file"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                  <label htmlFor="excel-file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
                      disabled={uploading}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      {uploading ? t('excelMapping.processing', 'Procesando...') : t('excelMapping.uploadExcel', 'Cargar Archivo Excel')}
                    </Button>
                  </label>
                </Box>
              )}

              {excelFile && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('excelMapping.file', 'Archivo')}: {excelFile.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('excelMapping.columnsFound', 'Columnas encontradas')}: {excelColumns.length}
                  </Typography>
                </Box>
              )}

              {config && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('excelMapping.file', 'Archivo')}: {config.excelFileName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('excelMapping.columns', 'Columnas')}: {config.excelColumns.length}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Mapeo de campos */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  {t('excelMapping.fieldMapping', 'Mapeo de Campos')}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addField}
                  size="small"
                >
                  {t('excelMapping.addField', 'Agregar Campo')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setMappingFields(prev => [...prev, { targetField: '', targetFieldLabel: '', isRequired: false, custom: true } as any])}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  {t('excelMapping.addCustomField', 'Agregar Campo Manual')}
                </Button>
              </Box>

              <Grid container spacing={2}>
                {mappingFields.map((field, index) => (
                  <Grid size={{ xs: 12 }} key={index}>
                    <Card variant="outlined">
                      <CardContent>
                        <Grid container spacing={2} alignItems="center">
                          <Grid size={{ xs: 12, md: 3 }}>
                            {!field.custom ? (
                              <FormControl fullWidth>
                                <InputLabel>{t('excelMapping.targetField', 'Campo Objetivo')}</InputLabel>
                                <Select
                                  value={targetFields.some(tf => tf.targetField === field.targetField) ? field.targetField : ''}
                                  onChange={(e) => {
                                    const selectedField = targetFields.find(f => f.targetField === e.target.value);
                                    handleFieldMappingChange(index, {
                                      targetField: e.target.value,
                                      targetFieldLabel: selectedField?.targetFieldLabel || '',
                                      isRequired: selectedField?.isRequired || false,
                                    });
                                  }}
                                  label={t('excelMapping.targetField', 'Campo Objetivo')}
                                  disabled={targetFields.length === 0}
                                >
                                  {targetFields.map((targetField) => (
                                    <MenuItem key={targetField.targetField} value={targetField.targetField}>
                                      {targetField.targetFieldLabel}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <TextField
                                fullWidth
                                label={t('excelMapping.targetField', 'Campo Objetivo')}
                                value={field.targetField || ''}
                                onChange={(e) => handleFieldMappingChange(index, { targetField: e.target.value })}
                                required
                              />
                            )}
                          </Grid>

                          <Grid size={{ xs: 12, md: 3 }}>
                            {!field.custom ? (
                              <TextField
                                fullWidth
                                label={t('excelMapping.targetFieldLabel', 'Etiqueta campo objetivo')}
                                value={field.targetFieldLabel || ''}
                                onChange={(e) => handleFieldMappingChange(index, { targetFieldLabel: e.target.value })}
                                disabled={!field.custom}
                              />
                            ) : (
                              <TextField
                                fullWidth
                                label={t('excelMapping.targetFieldLabel', 'Etiqueta campo objetivo')}
                                value={field.targetFieldLabel || ''}
                                onChange={(e) => handleFieldMappingChange(index, { targetFieldLabel: e.target.value })}
                              />
                            )}
                          </Grid>

                          <Grid size={{ xs: 12, md: 3 }}>
                            <FormControl fullWidth>
                              <InputLabel>{t('excelMapping.excelColumn', 'Columna Excel')}</InputLabel>
                              <Select
                                value={field.excelColumn || ''}
                                onChange={(e) => handleFieldMappingChange(index, { excelColumn: e.target.value })}
                                label={t('excelMapping.excelColumn', 'Columna Excel')}
                                disabled={field.source === 'nextcloud' || field.source === 'constant'}
                              >
                                <MenuItem value="">
                                  <em>{t('excelMapping.unmapped', 'Sin mapear')}</em>
                                </MenuItem>
                                {excelColumns.map((column) => (
                                  <MenuItem key={column} value={column}>
                                    {column}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid size={{ xs: 12, md: 3 }}>
                            <FormControl fullWidth>
                              <InputLabel>{t('excelMapping.source', 'Origen')}</InputLabel>
                              <Select
                                value={field.source || 'excel'}
                                onChange={(e) => handleFieldMappingChange(index, { source: e.target.value as any })}
                                label={t('excelMapping.source', 'Origen')}
                              >
                                <MenuItem value="excel">{t('excelMapping.sourceExcel', 'Excel')}</MenuItem>
                                <MenuItem value="nextcloud">{t('excelMapping.sourceNextcloud', 'Nextcloud (auto)')}</MenuItem>
                                <MenuItem value="constant">{t('excelMapping.sourceConstant', 'Constante')}</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid size={{ xs: 12, md: 2 }}>
                            <TextField
                              fullWidth
                              label={t('excelMapping.defaultValue', 'Valor por defecto')}
                              value={field.defaultValue || ''}
                              onChange={(e) => handleFieldMappingChange(index, { defaultValue: e.target.value })}
                              size="small"
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 2 }}>
                            <TextField
                              fullWidth
                              label={t('excelMapping.constantValue', 'Valor constante')}
                              value={field.constantValue || ''}
                              onChange={(e) => handleFieldMappingChange(index, { constantValue: e.target.value })}
                              size="small"
                              disabled={field.source !== 'constant'}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 2 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {field.isRequired && (
                                <Chip label={t('excelMapping.required', 'Requerido')} color="error" size="small" />
                              )}
                              <IconButton
                                color="error"
                                onClick={() => removeField(index)}
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Vista previa */}
        {excelPreview.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Vista Previa del Archivo Excel
                </Typography>
                <Box overflow="auto">
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        {excelColumns.map((column) => (
                          <th key={column} style={{ padding: '8px', border: '1px solid #ddd' }}>
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {excelPreview.map((row, index) => (
                        <tr key={index}>
                          {excelColumns.map((column) => (
                            <td key={column} style={{ padding: '8px', border: '1px solid #ddd' }}>
                              {row[column] || ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ExcelMappingConfigForm;
